import React, { useState, useEffect } from 'react';
import Button from '../../../components/base/Button';
import Card from '../../../components/base/Card';
import { X, Save, DollarSign, Calendar, Tag, FileText } from 'lucide-react';
import type { Presupuesto } from '../../../shared/types/presupuestos';

interface FormularioPresupuestoProps {
  presupuesto?: Presupuesto;
  onGuardar: (datos: Omit<Presupuesto, 'id' | 'userId' | 'fechaCreacion' | 'fechaActualizacion'>) => Promise<void>;
  onCancelar: () => void;
  isOpen: boolean;
  className?: string;
}

const CATEGORIAS_PREDEFINIDAS = [
  'Alimentación',
  'Transporte',
  'Vivienda',
  'Entretenimiento',
  'Salud',
  'Educación',
  'Ropa',
  'Tecnología',
  'Viajes',
  'Otros'
];

const PERIODOS = [
  { value: 'semanal', label: 'Semanal' },
  { value: 'mensual', label: 'Mensual' },
  { value: 'anual', label: 'Anual' }
];

export const FormularioPresupuesto: React.FC<FormularioPresupuestoProps> = ({
  presupuesto,
  onGuardar,
  onCancelar,
  isOpen,
  className = ''
}) => {
  const [formData, setFormData] = useState({
    categoria: '',
    monto: '',
    periodo: 'mensual',
    fechaInicio: '',
    fechaFin: '',
    descripcion: '',
    activo: true
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Calcular fechas automáticamente basado en el período
  const calcularFechaFin = (fechaInicio: string, periodo: string) => {
    if (!fechaInicio) return '';
    
    const fecha = new Date(fechaInicio);
    
    switch (periodo) {
      case 'semanal':
        fecha.setDate(fecha.getDate() + 7);
        break;
      case 'mensual':
        fecha.setMonth(fecha.getMonth() + 1);
        break;
      case 'anual':
        fecha.setFullYear(fecha.getFullYear() + 1);
        break;
    }
    
    return fecha.toISOString().split('T')[0];
  };

  // Efecto para inicializar el formulario
  useEffect(() => {
    if (presupuesto) {
      setFormData({
        categoria: presupuesto.categoria,
        monto: presupuesto.monto.toString(),
        periodo: presupuesto.periodo,
        fechaInicio: presupuesto.fechaInicio.toISOString().split('T')[0],
        fechaFin: presupuesto.fechaFin.toISOString().split('T')[0],
        descripcion: presupuesto.descripcion || '',
        activo: presupuesto.activo
      });
    } else {
      // Valores por defecto para nuevo presupuesto
      const hoy = new Date().toISOString().split('T')[0];
      const fechaFin = calcularFechaFin(hoy, 'mensual');
      
      setFormData({
        categoria: '',
        monto: '',
        periodo: 'mensual',
        fechaInicio: hoy,
        fechaFin: fechaFin,
        descripcion: '',
        activo: true
      });
    }
    
    setErrors({});
  }, [presupuesto, isOpen]);

  // Actualizar fecha fin cuando cambie la fecha inicio o período
  useEffect(() => {
    if (formData.fechaInicio && !presupuesto) {
      const nuevaFechaFin = calcularFechaFin(formData.fechaInicio, formData.periodo);
      setFormData(prev => ({ ...prev, fechaFin: nuevaFechaFin }));
    }
  }, [formData.fechaInicio, formData.periodo, presupuesto]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Limpiar error del campo cuando el usuario empiece a escribir
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validarFormulario = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.categoria.trim()) {
      newErrors.categoria = 'La categoría es obligatoria';
    }

    if (!formData.monto || parseFloat(formData.monto) <= 0) {
      newErrors.monto = 'El monto debe ser mayor a 0';
    }

    if (!formData.fechaInicio) {
      newErrors.fechaInicio = 'La fecha de inicio es obligatoria';
    }

    if (!formData.fechaFin) {
      newErrors.fechaFin = 'La fecha de fin es obligatoria';
    }

    if (formData.fechaInicio && formData.fechaFin && 
        new Date(formData.fechaInicio) >= new Date(formData.fechaFin)) {
      newErrors.fechaFin = 'La fecha de fin debe ser posterior a la fecha de inicio';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validarFormulario()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onGuardar({
        categoria: formData.categoria.trim(),
        monto: parseFloat(formData.monto),
        periodo: formData.periodo as 'semanal' | 'mensual' | 'anual',
        fechaInicio: new Date(formData.fechaInicio),
        fechaFin: new Date(formData.fechaFin),
        descripcion: formData.descripcion.trim(),
        activo: formData.activo
      });
    } catch (error) {
      console.error('Error guardando presupuesto:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className={`w-full max-w-md max-h-[90vh] overflow-y-auto ${className}`}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {presupuesto ? 'Editar Presupuesto' : 'Nuevo Presupuesto'}
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancelar}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-5 h-5" />
          </Button>
        </div>

        {/* Formulario */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Categoría */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Tag className="w-4 h-4 mr-2" />
              Categoría
            </label>
            <select
              value={formData.categoria}
              onChange={(e) => handleInputChange('categoria', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.categoria ? 'border-red-500' : 'border-gray-300'
              }`}
            >
              <option value="">Selecciona una categoría</option>
              {CATEGORIAS_PREDEFINIDAS.map(categoria => (
                <option key={categoria} value={categoria}>
                  {categoria}
                </option>
              ))}
            </select>
            {errors.categoria && (
              <p className="mt-1 text-sm text-red-600">{errors.categoria}</p>
            )}
          </div>

          {/* Monto */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="w-4 h-4 mr-2" />
              Monto Presupuestado
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.monto}
              onChange={(e) => handleInputChange('monto', e.target.value)}
              placeholder="0.00"
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                errors.monto ? 'border-red-500' : 'border-gray-300'
              }`}
            />
            {errors.monto && (
              <p className="mt-1 text-sm text-red-600">{errors.monto}</p>
            )}
          </div>

          {/* Período */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Calendar className="w-4 h-4 mr-2" />
              Período
            </label>
            <select
              value={formData.periodo}
              onChange={(e) => handleInputChange('periodo', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {PERIODOS.map(periodo => (
                <option key={periodo.value} value={periodo.value}>
                  {periodo.label}
                </option>
              ))}
            </select>
          </div>

          {/* Fechas */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Inicio
              </label>
              <input
                type="date"
                value={formData.fechaInicio}
                onChange={(e) => handleInputChange('fechaInicio', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.fechaInicio ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.fechaInicio && (
                <p className="mt-1 text-sm text-red-600">{errors.fechaInicio}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fecha Fin
              </label>
              <input
                type="date"
                value={formData.fechaFin}
                onChange={(e) => handleInputChange('fechaFin', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.fechaFin ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.fechaFin && (
                <p className="mt-1 text-sm text-red-600">{errors.fechaFin}</p>
              )}
            </div>
          </div>

          {/* Descripción */}
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <FileText className="w-4 h-4 mr-2" />
              Descripción (opcional)
            </label>
            <textarea
              value={formData.descripcion}
              onChange={(e) => handleInputChange('descripcion', e.target.value)}
              placeholder="Describe el objetivo de este presupuesto..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
            />
          </div>

          {/* Estado activo */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id="activo"
              checked={formData.activo}
              onChange={(e) => handleInputChange('activo', e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="activo" className="ml-2 text-sm text-gray-700">
              Presupuesto activo
            </label>
          </div>

          {/* Botones */}
          <div className="flex space-x-3 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              <Save className="w-4 h-4 mr-2" />
              {isSubmitting ? 'Guardando...' : 'Guardar'}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              onClick={onCancelar}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};